import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { DoseId } from '~/domain/changes/ids';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { DoseProvenance } from '~/domain/projections/views';
import { useDoseListView } from '~/hooks/domain/useDoseListView';
import { currentDoseIdState } from '~/state/atoms/dose/doseAtoms';
import useSelectedPatient from '../patient/useSelectedPatient';

export type AvailableDose = {
    /** Stable domain id — prefer this over the SOP UID as a React key. */
    id: DoseId;
    sopInstanceUID: string;
    imageUid?: string;
    label?: string;
    provenance: DoseProvenance;
    isLoaded: boolean;
    /**
     * The live entity, for handing back to the application layer
     * (`setEnvironmentDose`, `deleteDose`). Never store this in Recoil and
     * never mutate it — dose writes go through `DoseService`.
     */
    item?: DoseMatrix;
};

/**
 * Doses available for selection, projected from the patient aggregate and
 * paired with their live entity for the commands that still take one.
 *
 * @param showSelected Include the currently selected dose in the result.
 */
export default function useAvailableDoses(showSelected = false) {
    const patient = useSelectedPatient();
    const doseViews = useDoseListView();
    const currentDoseId = useRecoilValue(currentDoseIdState);

    return useMemo((): AvailableDose[] => {
        if (!patient) {
            return [];
        }
        return doseViews
            .filter((view) => showSelected || view.id !== currentDoseId)
            .map((view) => ({
                id: view.id,
                sopInstanceUID: view.sopInstanceUid,
                imageUid: view.imageUid,
                label: view.label,
                provenance: view.provenance,
                isLoaded: true,
                item: patient.doses.find((dose) => dose.id === view.id),
            }));
    }, [patient, doseViews, currentDoseId, showSelected]);
}
